import { defineStore } from "pinia";
import type { ProductsList } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { useWarehousesAndStores } from "./warehousesAndStores";
import { userStore } from "./user";

interface State {
	wishlistItems: number[];
	wishlistProducts: ProductsList | null;
}

export const useWishlistStore = defineStore({
	id: "wishListStore",
	state: (): State => {
		return {
			wishlistItems: [],
			wishlistProducts: null
		};
	},
	actions: {
		async updateWishList(value: number) {
			if (this.wishlistItems.includes(value)) {
				this.wishlistItems.splice(this.wishlistItems.indexOf(value), 1);
			} else {
				this.wishlistItems = [...this.wishlistItems, value];
			}

			await this.fetchWishlistProducts(this.wishlistItems);
			if (await useLoadUser().userHasSession()) {
				const userId = userStore()?.user?.Id;
				if (this.wishlistItems.length) {
					return await useNorcePwa().updateWishlist({
						wishlist: this.wishlistItems,
						userId: userId!
					});
				}

				return await useNorcePwa().deleteWishlist({
					userId: userId!
				});
			}
		},
		// Empty the whole list in one go (used by wishlist-page templates) —
		// mirrors updateWishList's session handling without N per-item calls.
		async clearWishlist() {
			const snapshot = this.wishlistItems;
			this.wishlistItems = [];
			await this.fetchWishlistProducts(this.wishlistItems);
			try {
				if (await useLoadUser().userHasSession()) {
					const userId = userStore()?.user?.Id;
					return await useNorcePwa().deleteWishlist({ userId: userId! });
				}
			} catch (error) {
				// Roll back the optimistic clear so the UI matches the server.
				this.wishlistItems = snapshot;
				await this.fetchWishlistProducts(this.wishlistItems);
				throw error;
			}
		},
		async initWishlist() {
			const userId = userStore()?.user?.Id;
			const wishlistCached = await useNorcePwa().getWishlist({
				userId: userId!
			});

			if (
				wishlistCached &&
				typeof wishlistCached === "string" &&
				wishlistCached?.length
			) {
				this.wishlistItems = [
					...new Set([
						...this.wishlistItems,
						...wishlistCached.split(",").map(Number)
					])
				];
			}

			await this.fetchWishlistProducts(this.wishlistItems);
		},
		async updateWishlistOnLogIn() {
			// This action runs inside the SSR app-init chain (useInitApp →
			// useLoadUser → userStore.setUser), where the Nuxt context does NOT
			// survive an await — a post-await useNorcePwa()/useNuxtApp() throws
			// "nuxt instance unavailable". Capture every composable BEFORE the
			// first await (same pattern as useCartInit and useLoadUser's locale).
			const { userHasSession } = useLoadUser();
			const { getWishlist, updateWishlist, listProductsByIds } = useNorcePwa();
			const fetchCtx = {
				locale: useNuxtApp().$i18n.locale.value,
				listProductsByIds,
				getStores: useWarehousesAndStores()?.getStores,
				storeContext: useNorceStore().storeContext
			};
			const userId = userStore()?.user?.Id;
			if (await userHasSession()) {
				let wishlistCached = await getWishlist({
					userId: userId!
				});

				wishlistCached = wishlistCached?.toString();

				if (wishlistCached && wishlistCached.length) {
					const wishlistCachedArray = wishlistCached.split(",").map(Number);
					const updatedWishlist = [
						...new Set([...this.wishlistItems, ...wishlistCachedArray])
					];

					await updateWishlist({
						wishlist: updatedWishlist,
						userId: userId!
					});
					this.wishlistItems = updatedWishlist;
				} else {
					await updateWishlist({
						wishlist: this.wishlistItems,
						userId: userId!
					});
				}

				await this.fetchWishlistProducts(this.wishlistItems, fetchCtx);
			}
		},
		async fetchWishlistProducts(
			wishlistItems: number[],
			// Pre-captured composable results for post-await callers: invoked
			// after an await in the SSR init chain (updateWishlistOnLogIn) the
			// Nuxt context is gone and useNuxtApp()/useNorcePwa() would throw,
			// so the caller captures these while the context still exists.
			ctx?: {
				locale: string;
				listProductsByIds: ReturnType<typeof useNorcePwa>["listProductsByIds"];
				getStores: ReturnType<typeof useWarehousesAndStores>["getStores"];
				storeContext: ReturnType<typeof useNorceStore>["storeContext"];
			}
		) {
			const locale = ctx?.locale ?? useNuxtApp().$i18n.locale.value;
			const listProductsByIds =
				ctx?.listProductsByIds ?? useNorcePwa().listProductsByIds;
			const getStores = ctx?.getStores ?? useWarehousesAndStores()?.getStores;
			const storeContext = ctx?.storeContext ?? useNorceStore().storeContext;

			if (wishlistItems.length) {
				this.wishlistProducts = await listProductsByIds({
					idSeed: wishlistItems.join(","),
					asVariants: true,
					storeSeed: getStores?.[locale]?.map((store) => store.Id),
					cultureCode: storeContext.cultureCode,
					currencyId: storeContext.currencyId.toString()
				});
				this.wishlistProducts.ItemCount = this.wishlistProducts.Items.length;
			} else {
				this.wishlistProducts = null;
			}
		}
	},
	getters: {
		getWishlistItems: (state) => state.wishlistItems
	},
	persist: {
		storage: persistedState.localStorage,
		pick: ["wishlistItems", "wishlistProducts"]
	}
});
